import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import {
    getAllSites,
    getScreeningDatesForQuery,
    loadAllSites,
    loadScreeningDatesByQuery,
} from '@vista-digital/occ-domain'
import type { CustomFilmAction, CustomFilmActions } from '@vista-digital/occ-ui'
import { createRoot } from 'react-dom/client'
import { cmsDataService } from '../../../services/cms.data.service'
import { navigationService } from '../../../services/navigation.service'
import type SecondaryAttributeMessagePropsApi from '../../SecondaryAttributeMessage/models'
import { SecondaryAttributeModal } from '../../SecondaryAttributeMessage/SecondaryAttributeModal'

const logger = getLogger('CustomFilmActions')

export const getCustomFilmActions = async (siteId: string | undefined) => {
    logger.value.trace('getCustomFilmActions', { siteId })

    const filmActions: CustomFilmActions = {}

    await addSecondaryAttributeActions(filmActions, siteId)
    addFilmPromotionsActions(filmActions)

    return filmActions
}

 
const addSecondaryAttributeActions = async (filmActions: CustomFilmActions, siteId: string | undefined) => {
    const secondaryAttributeId = cmsDataService.get().secondaryAttributeId
    const secondaryAttributeMessages = cmsDataService.get().secondaryAttributeMessages

    logger.value.trace('addSecondaryAttributeActions', {
        filmActions,
        siteId,
        secondaryAttributeId,
        secondaryAttributeMessages,
    })

    if (!secondaryAttributeId || secondaryAttributeMessages.length === 0) {
        logger.value.debug(
            'No secondary attribute id or messages configured. No secondary attribute messages will be added.'
        )
        return
    }

    let messageToDisplay: SecondaryAttributeMessagePropsApi | undefined = undefined

    // If we're on a cinema page, set the message to the config that includes the current cinema id, or undefined if no configs are for this cinema.
    // If we're not on a cinema page, use the first secondary attribute message with `Applicable To Experience And Film Showtime Pickers` enabled.

    if (siteId) {
        messageToDisplay = secondaryAttributeMessages.filter(
            m => !!m?.secondaryAttributePricingMessageCinemas?.sitePicker?.options?.includes(siteId)
        )?.[0]
    } else {
        messageToDisplay = secondaryAttributeMessages.filter(m => m.applicableToExperienceAndFilmShowtimePickers)?.[0]
    }

    if (!messageToDisplay) {
        logger.value.debug('No secondary messages configured. No secondary attribute messages will be added.')
        return
    }

    logger.value.debug('Determined message to display', messageToDisplay)

    const allFilmsWithSecondaryAttribute = await getAllFilmIdsWithSecondaryAttributeOnBusinessDate(
        secondaryAttributeId,
        siteId
    )

    const secondaryAttributeFilmAction: CustomFilmAction = {
        title: {
            text: messageToDisplay.secondaryAttributePricingMessageTitle ?? '',
            translations: [],
        },
        message: {
            text: messageToDisplay.secondaryAttributePricingMessageDescription ?? '',
            translations: [],
        },
        onClick: () => {
            logger.value.info(`Clicked ${messageToDisplay?.secondaryAttributePricingMessageTitle} film action`)

            if (
                !messageToDisplay?.secondaryAttributePricingModalMediaDesktop ||
                !messageToDisplay.secondaryAttributePricingModalMediaMobile ||
                !messageToDisplay.secondaryAttributePricingModalTitle
            ) {
                logger.value.debug(
                    'Secondary attribute message is missing modal media or title. No modal will be shown.'
                )
                return
            }

            const bodyElement = document.querySelector('body')!
            let targetElement = bodyElement.querySelector('#secondary-attribute-message-modal')

            if (!targetElement) {
                const newTarget = document.createElement('div')
                newTarget.id = 'secondary-attribute-message-modal'
                bodyElement.appendChild(newTarget)

                targetElement = newTarget
            }

            const root = createRoot(targetElement)
            root.render(
                <SecondaryAttributeModal
                    {...messageToDisplay}
                    onClose={() => {
                        root.unmount()
                        targetElement?.remove()
                    }}
                />
            )
        },
        iconName: messageToDisplay.secondaryAttributePricingIcon,
    }

    allFilmsWithSecondaryAttribute?.forEach(filmId => {
        addFilmAction(filmActions, filmId, secondaryAttributeFilmAction)
    })
}

 
const getAllFilmIdsWithSecondaryAttributeOnBusinessDate = async (
    secondaryAttributeId: string,
    siteId: string | undefined
): Promise<string[]> => {
    let siteIds = siteId ? [siteId] : undefined

    if (!siteId) {
        await loadAllSites()
        siteIds = getAllSites()?.map(site => site.id) ?? []
    }

    await loadScreeningDatesByQuery({ siteIds: siteIds ?? [] })
    const screeningDates = getScreeningDatesForQuery({ siteIds: siteIds ?? [] })

    if (!screeningDates) {
        return []
    }

    const allFilmsWithSecondaryAttribute = screeningDates
        ?.flatMap(s => s.filmScreenings)
        .filter(filmScreening =>
            filmScreening.sites.some(site => site.showtimeAttributeIds.includes(secondaryAttributeId))
        )
        .map(filmScreening => filmScreening.filmId)

    const allFilmsWithSecondaryAttributeSet = new Set(allFilmsWithSecondaryAttribute)

    logger.value.debug('Determined all film ids with secondary attribute for sites', {
        allFilmsWithSecondaryAttribute,
        siteIds,
    })

    return [...allFilmsWithSecondaryAttributeSet]
}

const addFilmPromotionsActions = (filmActions: CustomFilmActions) => {
    const filmPromotions = cmsDataService.get().filmPromotions

    for (const promo of filmPromotions) {
        for (const filmId of promo.filmIds) {
            const promoInfo: CustomFilmAction = {
                title: { text: promo.title || '  ', translations: [] },
                message: { text: promo.message || '  ', translations: [] },
                href: promo.url || '  ',
                onClick: _ => navigationService.goTo(promo.url),
                iconName: 'gift',
            }

            addFilmAction(filmActions, filmId, promoInfo)
        }
    }
}

const addFilmAction = (filmActions: CustomFilmActions, filmId: string, action: CustomFilmAction) => {
    logger.value.trace(`Adding custom film action for film id ${filmId}`, action)
    if (filmActions[filmId]) {
        filmActions[filmId] = [...filmActions[filmId], action]
    } else {
        filmActions[filmId] = [action]
    }
}
